import React, { useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { useForm } from "react-hook-form";
import { Button } from "@components/ui/Button";
import { Input } from "@components/ui/Input";
import { Card, CardBody } from "@components/ui/Card/Card";

export type ConfirmForgotPasswordFormData = {
  email: string;
  code: string;
  password: string;
};

type ConfirmForgotPasswordProps = {
  email?: string;
  onConfirm: (data: ConfirmForgotPasswordFormData) => Promise<void>;
} & RouteComponentProps;

export function ConfirmForgotPassword({
  email,
  onConfirm,
}: ConfirmForgotPasswordProps) {
  const [error, setError] = useState<string | null>();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfirmForgotPasswordFormData>({
    mode: "onBlur",
    defaultValues: {
      email,
    },
  });

  const onSubmit = async (data: ConfirmForgotPasswordFormData) => {
    setError(null);

    try {
      await onConfirm(data);
      reset();
    } catch (error) {
      if (error.message) {
        setError(error.message);
      } else {
        // TODO report this error somewhere
        setError("An unknown error occurred, please reach out to support.");
      }
    }
  };

  return (
    <Card title="Change your password">
      <CardBody>
        {email && (
          <div className="mb-6 text-green-700">
            An email with a verification code has been sent to {email}.
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <div className="mt-1">
                <Input
                  type="email"
                  {...register("email", {
                    required: {
                      value: true,
                      message: "Email is required",
                    },
                  })}
                  error={errors.email?.message}
                  readOnly={email !== undefined}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium text-gray-700"
              >
                Code
              </label>
              <div className="mt-1">
                <Input
                  type="text"
                  {...register("code", {
                    required: {
                      value: true,
                      message: "Code is required",
                    },
                  })}
                  error={errors.code?.message}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                New password
              </label>
              <div className="mt-1 text-xs text-gray-500">
                Password must be 12 characters and contain at least one
                uppercase, lowercase, number, and symbol.
              </div>
              <div className="mt-1">
                <Input
                  type="password"
                  {...register("password", {
                    required: {
                      value: true,
                      message: "Password is required",
                    },
                    minLength: {
                      value: 12,
                      message: "Password must have at least 12 characters",
                    },
                  })}
                  error={errors.password?.message}
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Button size="lg" type="submit" disabled={false}>
              Submit
            </Button>
          </div>
        </form>
        {error && <div className="text-red-600 mt-6">{error}</div>}
      </CardBody>
    </Card>
  );
}
